import {
  deleteField,
  type DocumentReference,
  type FieldValue,
  type Transaction,
} from 'firebase/firestore';
import type {
  ExpenseEntryData,
  SourceEntryBillingState,
  TimeEntryData,
  V3InvoiceData,
} from '@shared/types';

type SourceEntryData = TimeEntryData | ExpenseEntryData;
type SourceEntryMutationAction = 'update' | 'delete';
type SourceEntryBillingStatus = 'billable' | SourceEntryBillingState;

export const sourceEntryBillingStateUpdate = (
  status: SourceEntryBillingStatus,
): { billingState: SourceEntryBillingState | FieldValue } => ({
  billingState: status === 'unbillable' ? 'unbillable' : deleteField(),
});

export const guardTopLevelSourceEntryMutation = async <
  T extends SourceEntryData,
>(props: {
  transaction: Pick<Transaction, 'get'>;
  sourceRef: DocumentReference<T>;
  invoiceRefForId: (invoiceId: string) => DocumentReference<V3InvoiceData>;
  data?: Partial<T>;
  action: SourceEntryMutationAction;
}): Promise<void> => {
  const source = (await props.transaction.get(props.sourceRef)).data();
  const marksUnbillable = props.data?.billingState === 'unbillable';
  const assignsInvoice =
    props.data &&
    'invoiceId' in props.data &&
    typeof props.data.invoiceId === 'string';

  if (!source?.invoiceId) {
    if (marksUnbillable && assignsInvoice) {
      throw new Error(
        'Do not attach an entry to an invoice while marking it unbillable.',
      );
    }
    return;
  }

  const invoice = (
    await props.transaction.get(props.invoiceRefForId(source.invoiceId))
  ).data();
  if (!invoice) {
    throw new Error(
      'This entry is linked to a missing invoice. Refresh or ask support before editing it.',
    );
  }
  if (invoice.status === 'voided') {
    throw new Error(
      'This entry is linked to a voided invoice. Refresh or ask support before editing it.',
    );
  }
  throw new Error(
    props.action === 'delete'
      ? 'This entry has been billed and cannot be deleted.'
      : 'This entry has been billed and cannot be modified.',
  );
};
